import React from 'react';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    FormControl,
    IconButton,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    Stack,
    TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { ColumnDefinition, RELEVANT_TO_OPTIONS } from './formTypes';
import { isRowEmpty } from './formRules';

type ColumnDefinitionsProps = {
    rows: ColumnDefinition[];
    columns: string[];
    onChange: (rows: ColumnDefinition[]) => void;
};

const EMPTY_ROW: ColumnDefinition = { columns: [], definition: '', relevantTo: [] };

const titleCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const asArray = (value: string | string[]): string[] =>
    typeof value === 'string' ? value.split(',') : value;

export const ColumnDefinitions = ({ rows, columns, onChange }: ColumnDefinitionsProps) => {
    const updateRow = (index: number, patch: Partial<ColumnDefinition>) =>
        onChange(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));

    const removeRow = (index: number) => {
        const next = rows.filter((_, i) => i !== index);
        onChange(next.length > 0 ? next : [{ ...EMPTY_ROW }]);
    };

    const addRow = () => onChange([...rows, { ...EMPTY_ROW }]);

    const addDisabled = rows.some(isRowEmpty);

    return (
        <Stack spacing={2}>
            {rows.map((row, index) => (
                <Stack key={index} direction="row" spacing={2} alignItems="flex-start">
                    <FormControl sx={{ flex: 1 }}>
                        <InputLabel id={`column-${index}`}>Column</InputLabel>
                        <Select
                            labelId={`column-${index}`}
                            multiple
                            value={row.columns}
                            input={<OutlinedInput label="Column" />}
                            onChange={e => updateRow(index, { columns: asArray(e.target.value) })}
                            renderValue={selected => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {(selected as string[]).map(v => (
                                        <Chip key={v} label={v} size="small" />
                                    ))}
                                </Box>
                            )}
                        >
                            {columns.map(col => (
                                <MenuItem key={col} value={col}>
                                    <Checkbox checked={row.columns.indexOf(col) > -1} />
                                    <ListItemText primary={col} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Definition"
                        value={row.definition}
                        onChange={e => updateRow(index, { definition: e.target.value })}
                        sx={{ flex: 1 }}
                    />
                    <FormControl sx={{ flex: 1 }}>
                        <InputLabel id={`relevant-to-${index}`}>Relevant to</InputLabel>
                        <Select
                            labelId={`relevant-to-${index}`}
                            multiple
                            value={row.relevantTo}
                            input={<OutlinedInput label="Relevant to" />}
                            onChange={e => updateRow(index, { relevantTo: asArray(e.target.value) })}
                            renderValue={selected => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {(selected as string[]).map(v => (
                                        <Chip key={v} label={titleCase(v)} size="small" />
                                    ))}
                                </Box>
                            )}
                        >
                            {RELEVANT_TO_OPTIONS.map(opt => (
                                <MenuItem key={opt} value={opt}>
                                    <Checkbox checked={row.relevantTo.indexOf(opt) > -1} />
                                    <ListItemText primary={titleCase(opt)} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <IconButton
                        onClick={() => removeRow(index)}
                        aria-label="Remove column definition"
                        sx={{ mt: 1 }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Stack>
            ))}
            <Box>
                <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={addRow}
                    disabled={addDisabled}
                >
                    Add mapping
                </Button>
            </Box>
        </Stack>
    );
};
